import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import type { EChartsType } from 'echarts/types/dist/shared';
import { ChartConfig } from '../../models/chart-types';
import { buildLineChartOptions } from '../../builders/line.builder';
import { buildBarChartOptions } from '../../builders/bar.builder';
import { buildPieChartOptions } from '../../builders/pie.builder';
import { buildBoxplotOptions } from '../../builders/boxplot.builder';
import { buildMixedChartOptions } from '../../builders/mixed.builder';

@Component({
  selector: 'app-generic-chart',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './generic-chart.component.html',
})
export class GenericChartComponent implements OnInit, OnChanges {
  /**
   * Expose Object for template
   */
  Object = Object;
  
  /**
   * Chart configuration - drives the entire chart rendering
   */
  @Input({ required: true }) config!: ChartConfig;

  /**
   * Minimum height for the chart container (px).
   * Defaults to 400 to preserve existing behavior, but allows compact usage (e.g. dashboard cards).
   */
  @Input() minHeightPx = 400;

  /**
   * Loading state
   */
  @Input() loading = false;

  /**
   * Emits the ECharts instance when initialized
   */
  @Output() chartInit = new EventEmitter<EChartsType>();

  /**
   * Computed ECharts options
   */
  chartOptions: EChartsOption = {};

  /**
   * Chart builder registry
   * Maps chart types to their respective builder functions
   */
  private readonly chartBuilders: Record<string, (config: ChartConfig) => EChartsOption> = {
    line: buildLineChartOptions as (config: ChartConfig) => EChartsOption,
    timeseries: buildLineChartOptions as (config: ChartConfig) => EChartsOption,
    bar: buildBarChartOptions as (config: ChartConfig) => EChartsOption,
    pie: buildPieChartOptions as (config: ChartConfig) => EChartsOption,
    boxplot: buildBoxplotOptions as (config: ChartConfig) => EChartsOption,
    mixed: buildMixedChartOptions as (config: ChartConfig) => EChartsOption,
  };

  ngOnInit(): void {
    if (this.config) {
      this.buildChartOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config) {
      this.buildChartOptions();
    }
  }

  /**
   * Handle chart initialization from ngx-echarts
   * @param chart - The ECharts instance
   */
  onChartInit(chart: EChartsType): void {
    this.chartInit.emit(chart);
  }

  /**
   * Build ECharts options based on chart type using the builder registry
   */
  private buildChartOptions(): void {
    try {
      const builder = this.chartBuilders[this.config.type];
      
      if (builder) {
        this.chartOptions = builder(this.config);
      } else {
        console.error('Unsupported chart type:', this.config.type);
        this.chartOptions = {};
      }
    } catch (error) {
      console.error('Error building chart options:', error);
      this.chartOptions = {};
    }
  }
}

